/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/Compliance/ComplianceOverride.tsx
 * PURPOSE: Soft Compliance Override Portal - Manager Override with Audit Trail
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * DATE: 2025-12-28
 * COMPLIANCE: SDLC 6.3.1 + Expert 3 Feedback + Vietnamese Labor Law 2019
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Compliance
 * - Impact: Enable soft compliance with manager override capability
 * - Stakeholders: Branch Managers, HR Directors, Operations Directors
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Component, Data Fetching, Compliance Management, CSS Modules
 * - Dependencies: React 18, TypeScript, useCompliance hook
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/SOFT-COMPLIANCE-WARNING-DESIGN.md
 * - API: backend/app/api/v1/compliance_override.py
 * - Hook: frontend/src/hooks/useCompliance.ts
 * - Service: frontend/src/services/api/complianceService.ts
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { useCompliance } from '../../hooks/useCompliance';
import {
  ComplianceSeverity,
  OverrideReasonCategory,
  ComplianceResult,
  getSeverityLabelVi,
  getReasonCategoryLabelVi,
  canRoleOverride,
  OverrideRole,
} from '../../services/api/complianceService';
import styles from './ComplianceOverride.module.css';
import { toLocalISODate } from '@/utils/date';

// ============= Component State Types =============

interface OverrideFormState {
  isOpen: boolean;
  selectedViolation: ComplianceResult | null;
  reasonCategory: OverrideReasonCategory | null;
  reasonText: string;
}

interface ValidationFormState {
  employeeId: string;
  weeklyHours: number;
  dailyHours: number;
  consecutiveDays: number;
  monthlyOvertime: number;
  newShiftHours: number;
}

// ============= Compliance Override Portal Component =============

export const ComplianceOverride: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Compliance hook
  const {
    validationResult,
    isValidating,
    validationError,
    overrideResult,
    isOverriding,
    overrideError,
    auditLogs,
    isLoadingLogs,
    statistics,
    isLoadingStats,
    validateCompliance,
    requestOverride,
    fetchAuditLogs,
    fetchStatistics,
    clearValidation,
    hasBlockingViolations,
    hasOverridableViolations,
    canProceed,
  } = useCompliance();

  // Form states
  const [validationForm, setValidationForm] = useState<ValidationFormState>({
    employeeId: '',
    weeklyHours: 40,
    dailyHours: 8,
    consecutiveDays: 5,
    monthlyOvertime: 0,
    newShiftHours: 8,
  });

  const [overrideForm, setOverrideForm] = useState<OverrideFormState>({
    isOpen: false,
    selectedViolation: null,
    reasonCategory: null,
    reasonText: '',
  });

  const [activeTab, setActiveTab] = useState<'validate' | 'audit' | 'stats'>('validate');

  // User role mapping
  const userRole = useMemo((): OverrideRole => {
    const role = user?.role || 'branch_manager';
    const roleMap: Record<string, OverrideRole> = {
      branch_manager: 'branch_manager',
      hr_director: 'hr_director',
      operations_director: 'operations_director',
      ceo: 'ceo',
      admin: 'hr_director',
    };
    return roleMap[role] || 'branch_manager';
  }, [user?.role]);

  // Load data on mount
  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs({ page: 1, page_size: 20 });
    } else if (activeTab === 'stats') {
      fetchStatistics({});
    }
  }, [activeTab, fetchAuditLogs, fetchStatistics]);

  // Handle validation submit
  const handleValidate = async () => {
    if (!validationForm.employeeId) {
      alert('Vui lòng nhập mã nhân viên');
      return;
    }

    await validateCompliance({
      employee_id: validationForm.employeeId,
      current_weekly_hours: validationForm.weeklyHours,
      current_daily_hours: validationForm.dailyHours,
      current_consecutive_days: validationForm.consecutiveDays,
      current_monthly_overtime: validationForm.monthlyOvertime,
      new_shift_hours: validationForm.newShiftHours,
    });
  };

  // Handle override request
  const handleOverride = async () => {
    if (!overrideForm.selectedViolation || !overrideForm.reasonCategory || !overrideForm.reasonText) {
      alert('Vui lòng điền đầy đủ thông tin');
      return;
    }

    if (overrideForm.reasonText.length < 10) {
      alert('Lý do phải có ít nhất 10 ký tự');
      return;
    }

    const result = await requestOverride({
      employee_id: validationForm.employeeId,
      schedule_date: toLocalISODate(new Date()),
      violation_type: overrideForm.selectedViolation.violation_type,
      severity: overrideForm.selectedViolation.severity,
      reason_category: overrideForm.reasonCategory,
      reason_text: overrideForm.reasonText,
    });

    if (result) {
      setOverrideForm({
        isOpen: false,
        selectedViolation: null,
        reasonCategory: null,
        reasonText: '',
      });
      // Refresh validation
      handleValidate();
    }
  };

  // Open override modal
  const openOverrideModal = (violation: ComplianceResult) => {
    setOverrideForm({
      isOpen: true,
      selectedViolation: violation,
      reasonCategory: null,
      reasonText: '',
    });
  };

  // Get severity badge class
  const getSeverityBadgeClass = (severity: ComplianceSeverity): string => {
    switch (severity) {
      case 'INFO':
        return `${styles.severityBadge} ${styles.severityInfo}`;
      case 'WARNING':
        return `${styles.severityBadge} ${styles.severityWarning}`;
      case 'VIOLATION':
        return `${styles.severityBadge} ${styles.severityViolation}`;
      case 'CRITICAL':
        return `${styles.severityBadge} ${styles.severityCritical}`;
      default:
        return styles.severityBadge;
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'APPROVED':
        return `${styles.statusBadge} ${styles.statusApproved}`;
      case 'REJECTED':
        return `${styles.statusBadge} ${styles.statusRejected}`;
      default:
        return `${styles.statusBadge} ${styles.statusPending}`;
    }
  };

  // Get tab button class
  const getTabButtonClass = (tabId: string): string => {
    return activeTab === tabId
      ? `${styles.tabButton} ${styles.tabButtonActive}`
      : styles.tabButton;
  };

  // Get reason button class
  const getReasonButtonClass = (cat: OverrideReasonCategory): string => {
    return overrideForm.reasonCategory === cat
      ? `${styles.reasonButton} ${styles.reasonButtonActive}`
      : styles.reasonButton;
  };

  // Reason categories
  const reasonCategories: OverrideReasonCategory[] = [
    'SICK_LEAVE_COVER',
    'EMERGENCY_STAFFING',
    'SEASONAL_PEAK',
    'SPECIAL_EVENT',
    'EMPLOYEE_REQUEST',
    'OPERATIONAL_NECESSITY',
    'OTHER',
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <div className={styles.titleBadge}>
              <h1 className={styles.title}>⚖️ Compliance Override Portal</h1>
            </div>
            <div className={styles.roleBadge}>
              {userRole === 'hr_director' ? 'HR Director' :
               userRole === 'operations_director' ? 'Operations Director' :
               userRole === 'ceo' ? 'CEO' : 'Branch Manager'}
            </div>
          </div>

          <div className={styles.headerRight}>
            <button className={styles.backButton} onClick={() => navigate('/dashboard')}>
              ← Về Dashboard
            </button>
            <div className={styles.userName}>{user?.full_name || 'User'}</div>
            <button className={styles.logoutButton} onClick={() => logout()}>
              Đăng xuất
            </button>
          </div>
        </div>
      </header>

      <main className={styles.main}>
        {/* Vietnamese Labor Law Info Banner */}
        <section className={styles.lawBanner}>
          <h3 className={styles.lawBannerTitle}>📋 Quy Định Luật Lao Động 2019</h3>
          <div className={styles.lawGrid}>
            <div className={styles.lawItem}>
              <span className={styles.lawLabel}>Giờ tuần tối đa:</span>
              <span className={styles.lawValue}>48 giờ</span>
            </div>
            <div className={styles.lawItem}>
              <span className={styles.lawLabel}>Giờ ngày tối đa:</span>
              <span className={styles.lawValue}>12 giờ (8 + 4 OT)</span>
            </div>
            <div className={styles.lawItem}>
              <span className={styles.lawLabel}>OT tháng tối đa:</span>
              <span className={styles.lawValue}>40 giờ</span>
            </div>
            <div className={styles.lawItem}>
              <span className={styles.lawLabel}>Nghỉ giữa ca:</span>
              <span className={styles.lawValue}>≥12 giờ</span>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className={styles.tabsContainer}>
          {[
            { id: 'validate', label: '🔍 Kiểm Tra Tuân Thủ' },
            { id: 'audit', label: '📋 Nhật Ký Override' },
            { id: 'stats', label: '📊 Thống Kê' },
          ].map((tab) => (
            <button
              key={tab.id}
              className={getTabButtonClass(tab.id)}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Validation Tab */}
        {activeTab === 'validate' && (
          <section className={styles.tabContent}>
            {/* Validation Form */}
            <div className={styles.formSection}>
              <h3 className={styles.formTitle}>Kiểm tra tuân thủ cho ca làm mới</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Mã nhân viên *</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={validationForm.employeeId}
                    onChange={(e) => setValidationForm({ ...validationForm, employeeId: e.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Giờ tuần hiện tại</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    value={validationForm.weeklyHours}
                    onChange={(e) => setValidationForm({ ...validationForm, weeklyHours: Number(e.target.value) })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Giờ ngày hiện tại</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    value={validationForm.dailyHours}
                    onChange={(e) => setValidationForm({ ...validationForm, dailyHours: Number(e.target.value) })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Ngày làm liên tục</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    value={validationForm.consecutiveDays}
                    onChange={(e) => setValidationForm({ ...validationForm, consecutiveDays: Number(e.target.value) })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>OT tháng hiện tại</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    value={validationForm.monthlyOvertime}
                    onChange={(e) => setValidationForm({ ...validationForm, monthlyOvertime: Number(e.target.value) })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Ca mới (giờ) *</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    value={validationForm.newShiftHours}
                    onChange={(e) => setValidationForm({ ...validationForm, newShiftHours: Number(e.target.value) })}
                  />
                </div>
              </div>
              <button
                className={styles.validateButton}
                onClick={handleValidate}
                disabled={isValidating}
              >
                {isValidating ? 'Đang kiểm tra...' : '🔍 Kiểm Tra Tuân Thủ'}
              </button>
            </div>

            {/* Validation Error */}
            {validationError && (
              <div className={styles.errorBanner}>
                <span>⚠️ {validationError}</span>
              </div>
            )}

            {/* Validation Results */}
            {validationResult && (
              <div className={styles.resultsSection}>
                {/* Summary */}
                <div className={styles.resultsSummary}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Trạng thái:</span>
                    <span className={`${styles.summaryValue} ${validationResult.is_compliant ? styles.summaryValueSuccess : styles.summaryValueError}`}>
                      {validationResult.is_compliant ? '✅ Tuân thủ' : '❌ Vi phạm'}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Mức độ cao nhất:</span>
                    <span className={getSeverityBadgeClass(validationResult.highest_severity)}>
                      {getSeverityLabelVi(validationResult.highest_severity)}
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Tổng vi phạm:</span>
                    <span className={styles.summaryValue}>{validationResult.total_violations}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Có thể tiếp tục:</span>
                    <span className={`${styles.summaryValue} ${canProceed ? styles.summaryValueSuccess : styles.summaryValueError}`}>
                      {canProceed ? '✅ Có' : '❌ Không (có vi phạm nghiêm trọng)'}
                    </span>
                  </div>
                </div>

                {/* Blocking Violations */}
                {hasBlockingViolations && (
                  <div className={styles.violationsSection}>
                    <h4 className={styles.violationsTitle}>🚫 Vi Phạm Nghiêm Trọng (Không thể override)</h4>
                    <div className={styles.violationsList}>
                      {validationResult.blocking_violations.map((v, idx) => (
                        <div key={idx} className={styles.violationCard}>
                          <div className={styles.violationHeader}>
                            <span className={getSeverityBadgeClass(v.severity)}>
                              {getSeverityLabelVi(v.severity)}
                            </span>
                            <span className={styles.violationType}>{v.violation_type}</span>
                          </div>
                          <p className={styles.violationMessage}>{v.message_vi}</p>
                          <div className={styles.violationDetails}>
                            <span>Hiện tại: {v.current_value} | Giới hạn: {v.limit_value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Overridable Violations */}
                {hasOverridableViolations && (
                  <div className={styles.violationsSection}>
                    <h4 className={styles.violationsTitle}>⚠️ Vi Phạm Có Thể Override</h4>
                    <div className={styles.violationsList}>
                      {validationResult.overridable_violations.map((v, idx) => (
                        <div key={idx} className={styles.violationCard}>
                          <div className={styles.violationHeader}>
                            <span className={getSeverityBadgeClass(v.severity)}>
                              {getSeverityLabelVi(v.severity)}
                            </span>
                            <span className={styles.violationType}>{v.violation_type}</span>
                          </div>
                          <p className={styles.violationMessage}>{v.message_vi}</p>
                          <div className={styles.violationDetails}>
                            <span>Hiện tại: {v.current_value} | Giới hạn: {v.limit_value}</span>
                            {v.override_role && (
                              <span className={styles.overrideRole}>
                                Yêu cầu: {v.override_role}
                              </span>
                            )}
                          </div>
                          {v.can_override && canRoleOverride(userRole, v.severity) && (
                            <button
                              className={styles.overrideButton}
                              onClick={() => openOverrideModal(v)}
                            >
                              🔓 Yêu cầu Override
                            </button>
                          )}
                          {v.can_override && !canRoleOverride(userRole, v.severity) && (
                            <div className={styles.noPermission}>
                              Cần cấp cao hơn để override
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Informational */}
                {validationResult.informational.length > 0 && (
                  <div className={styles.violationsSection}>
                    <h4 className={styles.violationsTitle}>ℹ️ Thông Tin (Không cần override)</h4>
                    <div className={styles.violationsList}>
                      {validationResult.informational.map((v, idx) => (
                        <div key={idx} className={`${styles.violationCard} ${styles.violationCardInfo}`}>
                          <div className={styles.violationHeader}>
                            <span className={getSeverityBadgeClass(v.severity)}>
                              {getSeverityLabelVi(v.severity)}
                            </span>
                            <span className={styles.violationType}>{v.violation_type}</span>
                          </div>
                          <p className={styles.violationMessage}>{v.message_vi}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clear Button */}
                <button className={styles.clearButton} onClick={clearValidation}>
                  🗑️ Xóa kết quả
                </button>
              </div>
            )}
          </section>
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <section className={styles.tabContent}>
            {isLoadingLogs ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner} />
                <p>Đang tải nhật ký...</p>
              </div>
            ) : auditLogs && auditLogs.items.length > 0 ? (
              <div className={styles.tableSection}>
                <h3 className={styles.tableSectionTitle}>
                  📋 Nhật Ký Override ({auditLogs.total} bản ghi)
                </h3>
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>Thời gian</th>
                        <th className={styles.th}>Nhân viên</th>
                        <th className={styles.th}>Vi phạm</th>
                        <th className={styles.th}>Mức độ</th>
                        <th className={styles.th}>Lý do</th>
                        <th className={styles.th}>Người duyệt</th>
                        <th className={styles.th}>Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.items.map((log, idx) => (
                        <tr key={idx} className={styles.tr}>
                          <td className={styles.td}>
                            {new Date(log.created_at).toLocaleString('vi-VN')}
                          </td>
                          <td className={styles.td}>
                            <strong>{log.employee_name}</strong>
                            <div className={styles.employeeCode}>{log.employee_code}</div>
                          </td>
                          <td className={styles.td}>{log.violation_type}</td>
                          <td className={styles.td}>
                            <span className={getSeverityBadgeClass(log.severity)}>
                              {getSeverityLabelVi(log.severity)}
                            </span>
                          </td>
                          <td className={styles.td}>
                            <div>{getReasonCategoryLabelVi(log.reason_category)}</div>
                            <div className={styles.reasonText}>{log.reason_text}</div>
                          </td>
                          <td className={styles.td}>
                            <strong>{log.approved_by_name}</strong>
                            <div className={styles.approverRole}>{log.approved_by_role}</div>
                          </td>
                          <td className={styles.td}>
                            <span className={getStatusBadgeClass(log.status)}>
                              {log.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>Chưa có nhật ký override nào</p>
              </div>
            )}
          </section>
        )}

        {/* Statistics Tab */}
        {activeTab === 'stats' && (
          <section className={styles.tabContent}>
            {isLoadingStats ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner} />
                <p>Đang tải thống kê...</p>
              </div>
            ) : statistics ? (
              <div className={styles.statsSection}>
                <h3 className={styles.statsTitle}>📊 Thống Kê Override</h3>

                {/* Total */}
                <div className={styles.statsTotalCard}>
                  <span className={styles.statsTotalLabel}>Tổng số Override</span>
                  <span className={styles.statsTotalValue}>{statistics.total_overrides}</span>
                </div>

                {/* By Severity */}
                <div className={styles.statsGrid}>
                  <div className={styles.statsCard}>
                    <h4 className={styles.statsCardTitle}>Theo Mức Độ</h4>
                    <div className={styles.statsItems}>
                      {Object.entries(statistics.by_severity).map(([severity, count]) => (
                        <div key={severity} className={styles.statsItem}>
                          <span className={getSeverityBadgeClass(severity as ComplianceSeverity)}>
                            {getSeverityLabelVi(severity as ComplianceSeverity)}
                          </span>
                          <span className={styles.statsCount}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Status */}
                  <div className={styles.statsCard}>
                    <h4 className={styles.statsCardTitle}>Theo Trạng Thái</h4>
                    <div className={styles.statsItems}>
                      {Object.entries(statistics.by_status).map(([status, count]) => (
                        <div key={status} className={styles.statsItem}>
                          <span>{status}</span>
                          <span className={styles.statsCount}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Reason */}
                  <div className={styles.statsCard}>
                    <h4 className={styles.statsCardTitle}>Theo Lý Do</h4>
                    <div className={styles.statsItems}>
                      {Object.entries(statistics.by_reason_category).map(([reason, count]) => (
                        <div key={reason} className={styles.statsItem}>
                          <span>{getReasonCategoryLabelVi(reason as OverrideReasonCategory)}</span>
                          <span className={styles.statsCount}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Role */}
                  <div className={styles.statsCard}>
                    <h4 className={styles.statsCardTitle}>Theo Người Duyệt</h4>
                    <div className={styles.statsItems}>
                      {Object.entries(statistics.by_approver_role).map(([role, count]) => (
                        <div key={role} className={styles.statsItem}>
                          <span>{role}</span>
                          <span className={styles.statsCount}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>Không có dữ liệu thống kê</p>
              </div>
            )}
          </section>
        )}
      </main>

      {/* Override Modal */}
      {overrideForm.isOpen && overrideForm.selectedViolation && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>🔓 Yêu Cầu Override</h3>
              <button
                className={styles.modalClose}
                onClick={() => setOverrideForm({ ...overrideForm, isOpen: false })}
              >
                ×
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Violation Info */}
              <div className={styles.modalSection}>
                <h4 className={styles.modalSectionTitle}>Vi phạm được chọn</h4>
                <div className={styles.violationCard}>
                  <div className={styles.violationHeader}>
                    <span className={getSeverityBadgeClass(overrideForm.selectedViolation.severity)}>
                      {getSeverityLabelVi(overrideForm.selectedViolation.severity)}
                    </span>
                    <span className={styles.violationType}>
                      {overrideForm.selectedViolation.violation_type}
                    </span>
                  </div>
                  <p className={styles.violationMessage}>
                    {overrideForm.selectedViolation.message_vi}
                  </p>
                </div>
              </div>

              {/* Reason Category */}
              <div className={styles.modalSection}>
                <h4 className={styles.modalSectionTitle}>Lý do Override *</h4>
                <div className={styles.reasonGrid}>
                  {reasonCategories.map((cat) => (
                    <button
                      key={cat}
                      className={getReasonButtonClass(cat)}
                      onClick={() => setOverrideForm({ ...overrideForm, reasonCategory: cat })}
                    >
                      {getReasonCategoryLabelVi(cat)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason Text */}
              <div className={styles.modalSection}>
                <h4 className={styles.modalSectionTitle}>Chi tiết lý do * (tối thiểu 10 ký tự)</h4>
                <textarea
                  className={styles.reasonTextarea}
                  value={overrideForm.reasonText}
                  onChange={(e) => setOverrideForm({ ...overrideForm, reasonText: e.target.value })}
                  rows={4}
                />
                <div className={styles.charCount}>
                  {overrideForm.reasonText.length} / 500 ký tự
                </div>
              </div>

              {/* Override Error */}
              {overrideError && (
                <div className={styles.errorBanner}>
                  <span>⚠️ {overrideError}</span>
                </div>
              )}

              {/* Override Success */}
              {overrideResult && (
                <div className={styles.successBanner}>
                  <span>✅ {overrideResult.message}</span>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setOverrideForm({ ...overrideForm, isOpen: false })}
              >
                Hủy
              </button>
              <button
                className={styles.modalSubmitButton}
                onClick={handleOverride}
                disabled={isOverriding || !overrideForm.reasonCategory || overrideForm.reasonText.length < 10}
              >
                {isOverriding ? 'Đang xử lý...' : '✅ Xác Nhận Override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <span>Sprint 17 - Soft Compliance Override Portal</span>
          <span>Luật Lao Động 2019 | GOLIVE Dec 2025</span>
        </div>
      </footer>
    </div>
  );
};

export default ComplianceOverride;
